import { apiFetch } from './client';
import type { SharePointListName } from './sharePointLists';

/** Matches the shape Microsoft Graph returns; mirrored by the backend. */
export type ListItem<TFields extends object = Record<string, unknown>> = {
  id: string;
  createdDateTime: string;
  lastModifiedDateTime: string;
  webUrl?: string;
  fields: TFields & { id?: string };
};

export type ListQueryOptions = {
  /** OData $filter (e.g. `fields/Status_USR eq 'Activo'`). */
  filter?: string;
  /** OData $orderby (e.g. `fields/Modified desc`). */
  orderBy?: string;
  /** Page size, max 5000. Backend paginates automatically when needed. */
  top?: number;
};

export class SharePointListClient<TFields extends object = Record<string, unknown>> {
  constructor(public readonly list: SharePointListName) {}

  async listItems(options: ListQueryOptions = {}): Promise<ListItem<TFields>[]> {
    const res = await apiFetch<{ items: ListItem<TFields>[] }>(`/api/lists/${this.list}`, {
      query: {
        filter: options.filter,
        orderBy: options.orderBy,
        top: options.top,
      },
    });
    return res.items;
  }

  async getItem(id: string | number): Promise<ListItem<TFields>> {
    const res = await apiFetch<{ item: ListItem<TFields> }>(`/api/lists/${this.list}/${id}`);
    return res.item;
  }

  async createItem(fields: Partial<TFields>): Promise<ListItem<TFields>> {
    const res = await apiFetch<{ item: ListItem<TFields> }>(`/api/lists/${this.list}`, {
      method: 'POST',
      body: { fields },
    });
    return res.item;
  }

  async updateItem(id: string | number, fields: Partial<TFields>): Promise<ListItem<TFields>> {
    const res = await apiFetch<{ item: ListItem<TFields> }>(`/api/lists/${this.list}/${id}`, {
      method: 'PATCH',
      body: { fields },
    });
    return res.item;
  }

  async deleteItem(id: string | number): Promise<void> {
    await apiFetch<void>(`/api/lists/${this.list}/${id}`, { method: 'DELETE' });
  }
}

/** Escape a string for use inside an OData $filter literal. */
export function odataEscape(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}
